import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, FileText, Receipt } from 'lucide-react';
import { downloadHRDocument, formatDateShort } from './hrUtils';

const DocList = ({ docs, emptyText, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    {docs.length === 0 && <p className="text-sm text-slate-400 py-4">{emptyText}</p>}
    {docs.map((d) => (
      <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-cyan-300 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-5 h-5 text-cyan-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{d.title}</p>
            <p className="text-xs text-slate-400">{d.file_name} · {formatDateShort(d.created_at)}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => downloadHRDocument(d)} data-testid={`hr-doc-download-${d.id}`}>
          <Download className="w-4 h-4 mr-1" /> Download
        </Button>
      </div>
    ))}
  </div>
);

export const PayDocsTab = () => {
  const [paystubs, setPaystubs] = useState(null);
  const [taxDocs, setTaxDocs] = useState(null);

  useEffect(() => {
    Promise.all([
      hrAPI.listDocuments({ category: 'paystub' }),
      hrAPI.listDocuments({ category: 'tax' }),
    ]).then(([p, t]) => {
      setPaystubs(p.data.filter((d) => d.employee_id));
      setTaxDocs(t.data.filter((d) => d.employee_id));
    }).catch(() => { setPaystubs([]); setTaxDocs([]); });
  }, []);

  if (!paystubs || !taxDocs) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="hr-paydocs-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5 text-cyan-500" /> Pay Stubs
            <Badge variant="secondary" className="ml-auto">{paystubs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DocList docs={paystubs} emptyText="No pay stubs available yet. They will appear here once uploaded by HR." testId="hr-paystubs-list" />
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-500" /> Tax Documents
            <Badge variant="secondary" className="ml-auto">{taxDocs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DocList docs={taxDocs} emptyText="No tax documents available yet (T4s, receipts, etc. appear here)." testId="hr-taxdocs-list" />
        </CardContent>
      </Card>
    </div>
  );
};
